import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import { getLegacyUserInfo, clearLegacyUserData } from './legacyUserInfo';
import { getStore } from '@core/store';

class UserMigration {
  constructor() {
    this.migrationAttempted = false;
    this.legacyDomain = 'https://web.1xpay.co';
    this.currentLegacyData = null;
    this.setupMessageListener();
    this.setupMigrationListeners();
  }

  setupMessageListener() {
    window.addEventListener('message', (event) => {
      // cas 1: données depuis le context Capacitor (ancien Cordova)
      if (event.data?.type === 'LEGACY_USER_DATA') {
        console.log('[Migration] Données reçues du context Capacitor:', event.data.data);
        this.handleLegacyData(event.data.data);
      }

      // cas 2: réponse depuis l'iframe de l'ancien domaine
      if (event.data?.type === 'LEGACY_DB_DATA_RESPONSE') {
        console.log('[Migration] Données reçues de l\'iframe:', event.data.data);
        this.handleLegacyData(event.data.data);
      }
    });
  }

  setupMigrationListeners() {
    // utilisateur veut migrer
    bus.on(constants.EVENTS.LEGACY_MIGRATION.START_MIGRATION, (data) => {
      console.log('[Migration] Utilisateur confirme la migration');
      this.startMigrationFlow(data);
    });

    // utilisateur préfère continuer manuellement
    bus.on(constants.EVENTS.LEGACY_MIGRATION.CONFIRM_SKIP, async () => {
      console.log('[Migration] Utilisateur préfère continuer manuellement');
      await this.clearLegacyData();
      bus.emit(constants.EVENTS.LEGACY_MIGRATION.MIGRATION_COMPLETE);
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Données supprimées',
        body: 'Vos anciennes données ont été supprimées. Vous pouvez créer un nouveau compte.'
      });
    });
  }

  // point d'entrée principal - tente la migration
  async migrateUser() {
    if (this.migrationAttempted) return;
    this.migrationAttempted = true;

    console.log('[Migration] Tentative de migration des données utilisateur...');

    // cas 1: version web direct - essayer IndexedDB local
    const localData = await this.getLocalData();
    if (localData) {
      console.log('[Migration] Données trouvées en local (version web)');
      this.handleLegacyData(localData);
      return;
    }

    // cas 2: version iframe - charger l'ancien domaine pour accéder à son IndexedDB
    if (this.isInIframe()) {
      console.log('[Migration] Dans un iframe, tentative de chargement de l\'ancien domaine');
      this.requestDataFromLegacyDomain();
      return;
    }

    console.log('[Migration] Aucune donnée legacy trouvée');
  }

  // cas 1: récupérer depuis IndexedDB local (version web direct)
  async getLocalData() {
    try {
      const data = await getLegacyUserInfo();
      return data;
    } catch (error) {
      console.error('[Migration] Erreur IndexedDB local:', error);
      return null;
    }
  }

  // cas 2: charger l'ancien domaine dans un iframe caché
  requestDataFromLegacyDomain() {
    console.log('[Migration] Création iframe pour l\'ancien domaine');

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.id = 'legacy-migration-iframe';
    // charger une page spéciale sur l'ancien domaine qui contient les fonctions de migration
    iframe.src = `${this.legacyDomain}/migration-helper.html`;

    iframe.onload = () => {
      console.log('[Migration] Iframe chargé, demande des données');
      iframe.contentWindow.postMessage({
        type: 'REQUEST_LEGACY_DB_DATA'
      }, '*');
    };

    iframe.onerror = () => {
      console.error('[Migration] Erreur chargement iframe');
    };

    document.body.appendChild(iframe);

    // nettoyer après 30 secondes
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
        console.log('[Migration] Iframe nettoyé');
      }
    }, 30000);
  }

  isInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  // traiter les données legacy reçues
  async handleLegacyData(data) {
    if (!data || !data.email) {
      console.log('[Migration] Pas de donnée valide ou pas d\'email');
      return;
    }

    this.currentLegacyData = data;
    console.log('[Migration] Données utilisateur legacy:', data);

    // faire logout si l'utilisateur est connecté
    const userStore = getStore('user');
    const currentUser = userStore?.state?.current;

    if (currentUser?.email) {
      console.log('[Migration] Utilisateur connecté, déconnexion...');
      bus.emit(constants.EVENTS.USER.LOGOUT);
    }

    // essayer de récupérer le user sur le nouveau serveur avec l'userId
    if (data.userId) {
      console.log('[Migration] Tentative de récupération du user avec userId:', data.userId);
      const serverUser = await this.fetchUserFromServer(data.userId);

      if (serverUser) {
        console.log('[Migration] User trouvé sur le serveur, connexion directe');
        bus.emit(constants.EVENTS.USER.LOGIN_SUCCESS, serverUser);
        bus.emit(constants.EVENTS.LEGACY_MIGRATION.MIGRATION_COMPLETE);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Bienvenue',
          body: 'Votre compte a été récupéré avec succès'
        });
        // rediriger vers l'accueil
        if (window.$router) {
          window.$router.push('/home');
        }
        // nettoyer les anciennes données
        await this.clearLegacyData();
        return;
      }
    }

    // user non trouvé sur le serveur, afficher le modal de migration
    console.log('[Migration] User non trouvé sur le serveur, affichage du modal de migration');
    bus.emit(constants.EVENTS.LEGACY_MIGRATION.DETECTED, {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      reference: data.reference
    });
  }

  // récupérer le user depuis le serveur avec l'userId
  async fetchUserFromServer(userId) {
    try {
      const url = `${constants.ENDPOINTS.CORE.BASE}/user/1xpay/${userId}?group=lacasae`;
      const response = await fetch(url);

      if (!response.ok) {
        console.log('[Migration] User non trouvé sur le serveur (', response.status, ')');
        return null;
      }

      const userData = await response.json();
      console.log('[Migration] User récupéré du serveur:', userData);
      return userData?.data;
    } catch (error) {
      console.error('[Migration] Erreur lors de la récupération du user:', error);
      return null;
    }
  }

  // lancer le flow de migration OTP après confirmation utilisateur
  startMigrationFlow(data) {
    console.log('[Migration] Lancement du flow OTP pour:', data.email);

    // lancer le flow OTP pour la migration
    bus.emit(constants.EVENTS.OTP.SEND_OTP, {
      requestId: `migration-${Date.now()}`,
      channel: 'email',
      showContactInput: false, // pas d'input email, on utilise celui de la migration
      email: data.email,
      validate: true
    });

    // écouter la validation OTP pour continuer
    const handler = (otpData) => {
      if (otpData.requestId && otpData.requestId.startsWith('migration-')) {
        bus.off(constants.EVENTS.OTP.OTP_VALIDATED, handler);

        // OTP validé, afficher le modal pour le nouveau mot de passe
        bus.emit(constants.EVENTS.PASSWORD_RESET.SHOW_NEW_PASSWORD_INPUT, {
          email: data.email,
          isMigration: true,
          legacyData: this.currentLegacyData || {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            reference: data.reference
          }
        });
      }
    };
    bus.on(constants.EVENTS.OTP.OTP_VALIDATED, handler);
  }

  // nettoyer les anciennes données après migration réussie
  async clearLegacyData() {
    try {
      await clearLegacyUserData();
      console.log('[Migration] Anciennes données nettoyées avec succès');
    } catch (error) {
      console.error('[Migration] Erreur lors du nettoyage:', error);
    }
  }
}

export const userMigration = new UserMigration();
